import { format, parseISO } from 'date-fns';
import { Pencil } from 'lucide-react';
import type { SyncedBookingInfo, SyncedTableInfo } from '@/types/api';

interface BookingsListViewProps {
  bookings: SyncedBookingInfo[];
  tables: SyncedTableInfo[];
  isLoading: boolean;
  restaurantId: number;
  selectedBookingUuid?: string | null;
  onSelect?: (uuid: string | null) => void;
  onEdit?: (booking: SyncedBookingInfo) => void;
}

const getStatusBadge = (status: string | null) => {
  const statusColors: Record<string, string> = {
    PENDING: 'bg-amber-100 text-amber-800',
    CONFIRMED: 'bg-green-100 text-green-800',
    SEATED: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-gray-100 text-gray-800',
    CANCELLED: 'bg-red-100 text-red-800',
    NO_SHOW: 'bg-red-100 text-red-800',
  };

  const colorClass = status ? statusColors[status] || 'bg-gray-100 text-gray-800' : 'bg-gray-100 text-gray-800';

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {status || 'Unknown'}
    </span>
  );
};

const formatTimeRange = (startTime: string, endTime: string | null) => {
  const start = format(parseISO(startTime), 'HH:mm');
  if (!endTime) {
    return start;
  }
  const end = format(parseISO(endTime), 'HH:mm');
  return `${start} – ${end}`;
};

export default function BookingsListView({
  bookings,
  isLoading,
  selectedBookingUuid,
  onSelect,
  onEdit,
}: BookingsListViewProps) {
  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <p className="mt-2 text-sm text-gray-500">Loading bookings...</p>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">No bookings for this date.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Time
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Party
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Customer
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Tables
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Section
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Source
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Notes
            </th>
            {onEdit && (
              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {bookings.map((booking) => {
            const isSelected = !!booking.uuid && selectedBookingUuid === booking.uuid;
            return (
              <tr
                key={booking.uuid ?? booking.id}
                onClick={() => onSelect?.(isSelected ? null : booking.uuid ?? null)}
                className={`cursor-pointer ${isSelected ? 'bg-primary-50 hover:bg-primary-100' : 'hover:bg-gray-50'}`}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatTimeRange(booking.startTime, booking.endTime)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {booking.partySize ? `${booking.partySize}p` : '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div>
                    <div className="font-medium">{booking.customerName || '—'}</div>
                    <div className="text-gray-500">{booking.customerPhone || ''}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {booking.shopTableNames || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {booking.bookingSection || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(booking.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {booking.source || '—'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                  <div className="truncate" title={booking.notes || ''}>
                    {booking.notes || '—'}
                  </div>
                </td>
                {onEdit && (
                  <td className="px-3 py-4 whitespace-nowrap text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(booking);
                      }}
                      className="text-gray-400 hover:text-primary-600 p-1 rounded-md"
                      aria-label="Edit booking"
                      title="Edit booking"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}